import { Flex, SortButton } from '@gmg/gmg-react-components';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import styled from 'styled-components';
import LicenseSystemRowContainer from './LicenseSystemRowContainer';
import useSort from 'src/customHooks/useSort';
import { SortingOrder } from 'src/shared/TableStyle';
export interface LicenseListProps {
    licenseSystemList: Array<LicenseSystem>;
    isFilteringExpiredLicenses: boolean;
    filterValue: string;
    isAllowedToUseLicenseSystemsOffline: boolean;
};

const getSearchableValues = (ls: LicenseSystem, hideExpired: boolean): Array<string> => {
    const licenses = hideExpired
        ? ls.licenses.filter(lic => new Date(lic.expirationDate) >= new Date())
        : ls.licenses;
    return [
        ls.id,
        ls.computerName,
        ls.location,
        ...licenses.flatMap(lic => [
            lic.name,
            lic.installedApplicationDetails.version ?? '',
            ...lic.licenseOptions.map(option => option.name),
        ]),
    ].map(value => value.toLowerCase());
};

const LicenseManagementTable: FunctionComponent<LicenseListProps> = (props) => {
    const { t } = useTranslation();
    const [ids, setIds] = useState<Array<string>>([]);
    const { getSortFn } = useSort();
    const [sortingStatus, setSortingStatus] = useState<SortingOrder>({ direction: 'ASC', propertyName: 'name' });

    const handleSortButtonClick = (label: string) => {
        setSortingStatus({
            direction: sortingStatus.direction === 'ASC' ? 'DESC' : 'ASC',
            propertyName: label,
        });
    };

    const handleExpandLicenseSystemClick = (licenseSystemId: string) => {
        const isExpanded = ids.includes(licenseSystemId);

        isExpanded
            ? setIds(prev => prev.filter(id => id !== licenseSystemId))
            : setIds(prev => [...prev, licenseSystemId]);
    };

    const sortFn = getSortFn(sortingStatus.propertyName, sortingStatus.direction);
    const filterValue = props.filterValue.toLowerCase();
    const filterValueWithoutDots = filterValue.replace(/\./g, '');

    // Serial numbers are shown dotted (e.g. 2.173.121.722) but may be typed with or without dots.
    const shownLicenseSystems = props.licenseSystemList.filter(ls =>
        getSearchableValues(ls, props.isFilteringExpiredLicenses).some(value => value.includes(filterValue)) ||
        ls.id.toLowerCase().replace(/\./g, '').includes(filterValueWithoutDots),
    );

    const targetLicenseSystems = props.licenseSystemList
        .filter(ls => ls.licenseType === 'online')
        .map(({ id, userName, location, isTransferSupported, lastSeenDate }) => {
            return { id, userName, location, isTransferSupported, lastSeenDate };
        });

    return (
        <Wrapper>
            <Container aria-label="License systems" className="vertical-scrollbar">
                <Header className="px12_600_normal">
                    <Flex>{/*placeholder for alignment with arrow icon*/}</Flex>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.license_system_title', 'License system')}
                            onClick={() => handleSortButtonClick('id')}
                            direction={sortingStatus.propertyName === 'id' ? sortingStatus.direction : undefined}
                        />
                    </div>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.license_type_title', 'License type')}
                            onClick={() => handleSortButtonClick('licenseType')}
                            direction={sortingStatus.propertyName === 'licenseType' ? sortingStatus.direction : undefined}
                        />
                    </div>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.computer_title', 'Computer')}
                            onClick={() => handleSortButtonClick('computerName')}
                            direction={sortingStatus.propertyName === 'computerName' ? sortingStatus.direction : undefined}
                        />
                    </div>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.location_title', 'Location')}
                            onClick={() => handleSortButtonClick('location')}
                            direction={sortingStatus.propertyName === 'location' ? sortingStatus.direction : undefined}
                        />
                    </div>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.license_suc_title', 'SUC')}
                            onClick={() => handleSortButtonClick('hasActiveSuc')}
                            direction={sortingStatus.propertyName === 'hasActiveSuc' ? sortingStatus.direction : undefined}
                        />

                    </div>
                    <div role='columnheader'>
                        <SortButton
                            buttonLabel={t('LicenseManagementTable.columns.license_cp_go_title', 'ColorProof GO')}
                            onClick={() => handleSortButtonClick('cpGo')}
                            direction={sortingStatus.propertyName === 'cpGo' ? sortingStatus.direction : undefined}
                        />
                    </div>
                </Header>

                <Flex flexDirection="column" gap="16px">
                    {shownLicenseSystems
                        .sort(sortFn)
                        .map((licenseSystem, i) =>
                            <LicenseSystemRowContainer
                                key={licenseSystem.id + i}
                                licenseSystem={licenseSystem}
                                isExpanded={ids.includes(licenseSystem.id)}
                                isExpiredLicensesFilterActive={props.isFilteringExpiredLicenses}
                                onArrowClick={handleExpandLicenseSystemClick}
                                targetLicenseSystems={targetLicenseSystems}
                                isAllowedToUseLicenseSystemsOffline={props.isAllowedToUseLicenseSystemsOffline}
                            />,
                        )}
                </Flex>
            </Container>
        </Wrapper>
    );
};
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px;
    min-width: 100%;

    background-color:  ${props => props.theme.colors.backgroundPaper};
`;

const Container = styled.div`
    min-width: var(--product-table-width);
    max-width: var(--product-table-width);
    height: var(--card-style-table-max-height);
    border-radius: 8px;
    box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.10);
    background-color: ${props => props.theme.colors.background};
    padding: 0 var(--table-card-padding);

    &::-webkit-scrollbar-track {
        /* has to be set more exactly */
        margin-top: var(--table-scrollbar-track-margin-top);
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Header = styled.div`
    position: sticky;
    top: 0;
    display: grid;
    line-height: 2;
    /* First and second column should be aligned with the licenses accordion */
    grid-template-columns: 30px minmax(100px, 3fr) repeat(3, minmax(100px, 2fr)) repeat(1, minmax(50px, 1fr)) repeat(2, minmax(100px, 1fr));
    column-gap: 20px;
    align-items: center;
    height: var(--table-row-height);
    background-color: ${props => props.theme.colors.background};
    box-sizing: border-box;
    z-index: 30;
`;

export default LicenseManagementTable;
